import { getGoogleAnalyticsData, EventNameTypes, makeSegmentTrackCall } from 'client/javascript/analytics';
import { getLocale } from 'client/javascript/util/locale';
import { geoData } from 'client/javascript/geo';
import { varsFromServer } from 'client/javascript/client_side_injection_vars';
import { DRIFT_LANGUAGES, DRIFT_DETAILS } from 'client/javascript/drift/constants';
import { logError } from 'client/javascript/util/logger';
import qs from 'qs';

const loadDriftScript = () => {
  // From drift.js snippet (DO NOT MODIFY)
  /* eslint-disable */
  !(function() {
    var t = (window.driftt = window.drift = window.driftt || []);
    if (!t.init) {
      if (t.invoked) return void (window.console && console.error && console.error('Drift snippet included twice.'));
      (t.invoked = !0),
        (t.methods = ['identify', 'config', 'track', 'reset', 'debug', 'show', 'ping', 'page', 'hide', 'off', 'on']),
        (t.factory = function(e) {
          return function() {
            var n = Array.prototype.slice.call(arguments);
            return n.unshift(e), t.push(n), t;
          };
        }),
        t.methods.forEach(function(e) {
          t[e] = t.factory(e);
        }),
        (t.load = function(t) {
          var e = 3e5,
            n = Math.ceil(new Date() / e) * e,
            o = document.createElement('script');
          (o.type = 'text/javascript'),
            (o.async = !0),
            (o.crossorigin = 'anonymous'),
            (o.src = 'https://js.driftt.com/include/' + n + '/' + t + '.js');
          var i = document.getElementsByTagName('script')[0];
          i.parentNode.insertBefore(o, i);
        });
    }
  })();
  /* eslint-enable */
};

export function initializeDrift() {
  if (!window.drift) {
    loadDriftScript();

    window.drift.config({ locale: getDriftLocale() });
    window.drift.load(varsFromServer.driftChatKey);
    setupDrift();
  }
}

export function trackDrift(message) {
  window.drift.track(message);
}

export const setUserAttributesInDrift = attributes => {
  window.drift.on('ready', api => {
    api.setUserAttributes(attributes);
  });
};

export function getDriftLocale() {
  // Set drift language based on locale, default to English
  const { locale } = varsFromServer;
  const language = locale.split('-')[0];
  let driftLocale = 'en';
  if (DRIFT_LANGUAGES.includes(locale)) {
    driftLocale = locale;
  } else if (DRIFT_LANGUAGES.includes(language)) {
    driftLocale = language;
  }
  return driftLocale;
}

export function trackChatActivity(data) {
  makeSegmentTrackCall(EventNameTypes.chatActivity, data);
}

function driftOnWelcomeMessageOpen() {
  trackChatActivity({
    label: 'Welcome Message - Opened'
  });
}

function driftOnWelcomeMessageClose() {
  trackChatActivity({
    label: 'Welcome Message - Closed'
  });
}

function driftOnAwayMessageOpen() {
  trackChatActivity({
    label: 'Away Message - Opened'
  });
}

function driftOnAwayMessageClose() {
  trackChatActivity({
    label: 'Away Message - Closed'
  });
}

function driftOnCampaignOpen({ campaignId }) {
  trackChatActivity({
    label: 'Campaign - Opened',
    campaignId
  });
}

function driftOnCampaignDismiss(event) {
  trackChatActivity({
    label: 'Campaign - Dismissed',
    ...event.data
  });
}

function driftOnCampaignClick(event) {
  trackChatActivity({
    label: 'Campaign - CTA Clicked',
    ...event.data
  });
}

function driftOnCampaignSubmit(event) {
  trackChatActivity({
    label: 'Campaign - Submit',
    ...event.data
  });
}

function driftOnStartConversation(event) {
  trackChatActivity({
    label: 'Start Conversation',
    ...event.data
  });
}

const getConversationUserAttributes = async () => {
  const url = window.location.href;
  const splitUrl = url.split('?');
  const cleanUrl = splitUrl[0];
  const { gclid, dclid, gclsrc } = qs.parse(splitUrl[1]);
  const locale = getLocale();
  const { countryCode } = await geoData;
  return {
    Full_URL: url,
    Clean_URL: cleanUrl,
    Heap_Id: window.heap && window.heap.appid,
    Segment_Id: window.analyticsGlobals && window.analyticsGlobals.userId,
    Locale: locale,
    GCLID: gclid,
    DCLID: dclid,
    GCLSRC: gclsrc,
    Country_Code: countryCode,
    ...DRIFT_DETAILS
  };
};

async function driftOnConversationFirstInteraction() {
  const driftAttributes = await getConversationUserAttributes();
  try {
    const { gatrackid, gaclientid } = await getGoogleAnalyticsData();
    setUserAttributesInDrift({
      ...driftAttributes,
      GACLIENTID: gaclientid,
      GATRACKID: gatrackid
    });
  } catch (error) {
    logError(`[Drift] driftOnConversationFirstInteraction failed: ${error.message}`, error);
    setUserAttributesInDrift({
      ...driftAttributes
    });
  }
}

function driftOnSidebarOpen(event) {
  trackChatActivity({
    label: 'Sidebar Open',
    ...event.data
  });
}

function driftOnSidebarClose(event) {
  trackChatActivity({
    label: 'Sidebar Close',
    ...event.data
  });
}

function driftOnMessageSent(event) {
  trackChatActivity({
    label: 'Message Sent',
    ...event.data
  });
}

function driftOnMessage(event) {
  trackChatActivity({
    label: 'Message',
    ...event.data
  });
}

const driftOnEmailCapture = event => {
  if (!event || !event.data) {
    return false;
  }
  setUserAttributesInDrift({ email: event.data.email });
};

function driftOnSchedulingRequestMeeting(event) {
  trackChatActivity({
    label: 'Schedule Request',
    ...event.data
  });
}

function driftOnSchedulingMeetingBooked(event) {
  trackChatActivity({
    label: 'Schedule Booked',
    ...event.data
  });
}

function setInitialUserAttributes() {
  const queryParams = new URLSearchParams(window.location.search);
  setUserAttributesInDrift({
    recent_medium_c: queryParams.get('utm_medium'),
    recent_source_c: queryParams.get('utm_source'),
    recent_campaign_c: queryParams.get('utm_campaign'),
    recent_content_c: queryParams.get('utm_content'),
    recent_term_c: queryParams.get('utm_term')
  });
}

function bindDriftEventHandlers() {
  const { drift } = window;

  // Welcome Message Events
  drift.on('welcomeMessage:open', driftOnWelcomeMessageOpen);
  drift.on('welcomeMessage:close', driftOnWelcomeMessageClose);

  // Away Message Events
  drift.on('awayMessage:open', driftOnAwayMessageOpen);
  drift.on('awayMessage:close', driftOnAwayMessageClose);

  // Campaign Events
  drift.on('campaign:open', driftOnCampaignOpen);
  drift.on('campaign:dismiss', driftOnCampaignDismiss);
  drift.on('campaign:click', driftOnCampaignClick);
  drift.on('campaign:submit', driftOnCampaignSubmit);

  // Conversation Events
  drift.on('startConversation', driftOnStartConversation);
  drift.on('conversation:firstInteraction', driftOnConversationFirstInteraction);

  // Sidebar Handling
  drift.on('sidebarOpen', driftOnSidebarOpen);
  drift.on('sidebarClose', driftOnSidebarClose);

  // Message Events
  drift.on('message:sent', driftOnMessageSent);
  drift.on('message', driftOnMessage);

  // Email Capture Events
  drift.on('emailCapture', driftOnEmailCapture);

  // Scheduling Events
  drift.on('scheduling:requestMeeting', driftOnSchedulingRequestMeeting);
  drift.on('scheduling:meetingBooked', driftOnSchedulingMeetingBooked);
}

function setupDrift() {
  setInitialUserAttributes();
  bindDriftEventHandlers();
}
